import {useEffect, useMemo, useCallback, useState} from 'react';
import {Box, Table, TableBody, TableCell, TableRow, Typography, type SxProps} from '@mui/material';
import {useFsAdjustStore, useAdjustmentStore} from '@/store/audit';
import {useCompanyAnnualStore} from '@/store/audit/companyAnnualStore';
import {getWorkingPaper} from '@/services/auditService/auditWorkingPaperService';
import {useRegisterWpCheckSummary} from '@/pages/components/audit/adjustment/adjust/dialogs/basic/common/wpCheckSummaryContext';
import {useWpPeriodScopeContext} from '@/adjustment/hooks/WpPeriodScopeContext';
import {resolveWpIgnoreLastYear} from '@/utils/auditPeriodWpMulti';

const INTEREST_CODE = 'PL-FC-LI';
const BANK_LOAN_CODE = 'BS-CL-BLS';
const VEHICLE_LOAN_CODE = 'BS-CL-OFL';

export type InterestSourceType = 'bank' | 'vehicle';

export interface InterestSourceOverride {
    source: InterestSourceType;
    currentValue: number;
    lastYearValue: number;
}

export interface WpInterestPerCheckPayload {
    yoa: string;
    lastYoa: string;
    sx?: SxProps;
}

export interface WpInterestPerCheckProps {
    payload: WpInterestPerCheckPayload;
    sourceOverride?: InterestSourceOverride;
    annualId?: number;
    companyId?: number;
    currentPer?: (balanced: boolean) => void;
    lastYearPer?: (balanced: boolean) => void;
    /** Override Per PL current YOA (e.g. ProfitLoss total-mode YOA column). */
    auditedValueOverride?: number;
    /** Override Per PL last YOA; omit to keep perData.lastYearAudited. */
    lastYearAuditedOverride?: number;
}

const formatAmount = (value: number) => {
    return (Number(value) || 0).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

const toNum = (v: unknown) => Number(v) || 0;

/** Align with BankLoan Interest paid row: prefer interestYoaChange, else interestNext / interestYoa. */
const resolveBankInterestCurr = (sums: Record<string, unknown> | null | undefined): number => {
    if (!sums) return 0;
    if (sums.interestYoaChange != null) return toNum(sums.interestYoaChange);
    if (sums.interestCurrChange != null) return toNum(sums.interestCurrChange);
    return toNum(sums.interestNext ?? sums.interestYoa ?? sums.interestCurr);
};

const resolveBankInterestLast = (sums: Record<string, unknown> | null | undefined): number => {
    if (!sums) return 0;
    if (sums.interestLastYoaChange != null) return toNum(sums.interestLastYoaChange);
    if (sums.interestLastChange != null) return toNum(sums.interestLastChange);
    return toNum(sums.interestLastYoa ?? sums.interestLast);
};

const resolveVehicleInterestCurr = (sums: Record<string, unknown> | null | undefined): number => {
    if (!sums) return 0;
    if (sums.interestYoaChange != null) return toNum(sums.interestYoaChange);
    return toNum(sums.interestYoa ?? sums.hpInterest);
};

const resolveVehicleInterestLast = (sums: Record<string, unknown> | null | undefined): number => {
    if (!sums) return 0;
    if (sums.interestLastYoaChange != null) return toNum(sums.interestLastYoaChange);
    if (sums.hpInterestLastChange != null) return toNum(sums.hpInterestLastChange);
    return toNum(sums.interestLastYoa ?? sums.hpInterestLast);
};

const sumBankInterest = (
    bankLoans: Array<{sums?: Record<string, unknown>}> | undefined,
    field: 'curr' | 'last',
): number => {
    if (!Array.isArray(bankLoans)) return 0;
    return bankLoans.reduce((acc, loan) => {
        const resolve = field === 'curr' ? resolveBankInterestCurr : resolveBankInterestLast;
        return acc + resolve(loan?.sums);
    }, 0);
};

const sumVehicleInterest = (
    vehicleLoans: Array<{sums?: Record<string, unknown>}> | undefined,
    field: 'curr' | 'last',
): number => {
    if (!Array.isArray(vehicleLoans)) return 0;
    return vehicleLoans.reduce((acc, loan) => {
        const resolve = field === 'curr' ? resolveVehicleInterestCurr : resolveVehicleInterestLast;
        return acc + resolve(loan?.sums);
    }, 0);
};

const unwrapWpPayload = (result: unknown): Record<string, unknown> => {
    const data = Array.isArray(result) && result.length > 0 ? result[0] : result;
    return (data && typeof data === 'object' ? data : {}) as Record<string, unknown>;
};

export function WpInterestPerCheck({
    payload,
    sourceOverride,
    annualId,
    companyId,
    currentPer,
    lastYearPer,
    auditedValueOverride,
    lastYearAuditedOverride,
}: WpInterestPerCheckProps) {
    const isFirstAudit = useCompanyAnnualStore((s) => s.isCurrentFirstAudit());
    const periodScope = useWpPeriodScopeContext();
    const ignoreLastYear = resolveWpIgnoreLastYear(isFirstAudit, periodScope);
    const getPerData = useFsAdjustStore((s) => s.getPerData);
    const perData = getPerData(INTEREST_CODE);
    const headerAnnualId = useAdjustmentStore((s) => Number(s.header?.annualId || 0) || 0);

    const [backendSummary, setBackendSummary] = useState<Record<InterestSourceType, {finalTotal: number; lastYearTotal: number}>>({
        bank: {finalTotal: 0, lastYearTotal: 0},
        vehicle: {finalTotal: 0, lastYearTotal: 0},
    });

    const effectiveAnnualId = annualId || headerAnnualId;

    useEffect(() => {
        if (!effectiveAnnualId || !companyId) return;
        let cancelled = false;

        Promise.all([
            getWorkingPaper({companyId, annualId: effectiveAnnualId, code: BANK_LOAN_CODE}),
            getWorkingPaper({companyId, annualId: effectiveAnnualId, code: VEHICLE_LOAN_CODE}),
        ])
            .then(([bankResult, vehicleResult]) => {
                if (cancelled) return;
                const bankData = unwrapWpPayload(bankResult);
                const vehicleData = unwrapWpPayload(vehicleResult);
                setBackendSummary({
                    bank: {
                        finalTotal: sumBankInterest(bankData.bankLoans as any, 'curr'),
                        lastYearTotal: sumBankInterest(bankData.bankLoans as any, 'last'),
                    },
                    vehicle: {
                        finalTotal: sumVehicleInterest(vehicleData.vehicleLoans as any, 'curr'),
                        lastYearTotal: sumVehicleInterest(vehicleData.vehicleLoans as any, 'last'),
                    },
                });
            })
            .catch((err) => {
                if (!cancelled) console.warn('[WpInterestPerCheck] Failed to fetch interest summary:', err);
            });

        return () => {
            cancelled = true;
        };
    }, [effectiveAnnualId, companyId]);

    const auditedValue = useMemo(() => {
        if (auditedValueOverride != null && Number.isFinite(Number(auditedValueOverride))) {
            return toNum(auditedValueOverride);
        }
        return toNum(perData?.auditedValue);
    }, [auditedValueOverride, perData?.auditedValue]);
    const lastYearAudited = useMemo(() => {
        if (lastYearAuditedOverride != null && Number.isFinite(Number(lastYearAuditedOverride))) {
            return toNum(lastYearAuditedOverride);
        }
        return toNum(perData?.lastYearAudited);
    }, [lastYearAuditedOverride, perData?.lastYearAudited]);
    const statement = perData?.statement ?? 'PL';

    const getSummaryValue = useCallback((source: InterestSourceType, field: 'finalTotal' | 'lastYearTotal') => {
        if (sourceOverride && sourceOverride.source === source) {
            return field === 'finalTotal' ? sourceOverride.currentValue : sourceOverride.lastYearValue;
        }
        return backendSummary[source]?.[field] || 0;
    }, [sourceOverride, backendSummary]);

    const bankFinal = useMemo(() => getSummaryValue('bank', 'finalTotal'), [getSummaryValue]);
    const bankLastYear = useMemo(() => getSummaryValue('bank', 'lastYearTotal'), [getSummaryValue]);
    const vehicleFinal = useMemo(() => getSummaryValue('vehicle', 'finalTotal'), [getSummaryValue]);
    const vehicleLastYear = useMemo(() => getSummaryValue('vehicle', 'lastYearTotal'), [getSummaryValue]);

    const totalCurrentYear = useMemo(() => bankFinal + vehicleFinal, [bankFinal, vehicleFinal]);
    const totalLastYear = useMemo(() => bankLastYear + vehicleLastYear, [bankLastYear, vehicleLastYear]);

    const currentDiff = useMemo(() => totalCurrentYear - auditedValue, [totalCurrentYear, auditedValue]);
    const lastYearDiff = useMemo(() => totalLastYear - lastYearAudited, [totalLastYear, lastYearAudited]);

    const currentOk = useMemo(() => Math.abs(currentDiff) <= 0.01, [currentDiff]);
    const lastYearOk = useMemo(() => Math.abs(lastYearDiff) <= 0.01, [lastYearDiff]);

    useEffect(() => {
        if (currentPer) currentPer(currentOk);
    }, [currentPer, currentOk]);

    useEffect(() => {
        if (lastYearPer) lastYearPer(ignoreLastYear ? true : lastYearOk);
    }, [lastYearPer, lastYearOk, ignoreLastYear]);

    useRegisterWpCheckSummary({
        name: 'Loan Paid',
        yoa: payload.yoa,
        lastYoa: payload.lastYoa,
        currentBalanced: currentOk,
        lastYearBalanced: lastYearOk,
        ignoreLastYear,
    });

    const diffCellSx = (ok: boolean) => ({
        color: ok ? 'text.primary' : 'error.main',
        fontWeight: ok ? 500 : 700,
        textAlign: 'center',
    });

    const amountCellSx = {
        textAlign: 'center',
        fontWeight: 500,
    } as const;

    const labelCellSx = {
        fontWeight: 600,
        whiteSpace: 'nowrap',
    } as const;

    return (
        <Box sx={payload.sx}>
            <Table size="small">
                <TableBody>
                    <TableRow>
                        <TableCell/>
                        <TableCell align="center">
                            <Typography variant="body2" sx={{fontWeight: 600}}>
                                {payload.yoa}
                            </Typography>
                        </TableCell>
                        {!ignoreLastYear && (
                            <TableCell align="center">
                                <Typography variant="body2" sx={{fontWeight: 600}}>
                                    {payload.lastYoa}
                                </Typography>
                            </TableCell>
                        )}
                    </TableRow>

                    <TableRow>
                        <TableCell>
                            <Typography variant="body2" sx={labelCellSx}>
                                Bank Loan Paid
                            </Typography>
                        </TableCell>
                        <TableCell sx={amountCellSx}>{formatAmount(bankFinal)}</TableCell>
                        {!ignoreLastYear && (
                            <TableCell sx={amountCellSx}>{formatAmount(bankLastYear)}</TableCell>
                        )}
                    </TableRow>

                    <TableRow>
                        <TableCell>
                            <Typography variant="body2" sx={labelCellSx}>
                                Vehicle Loan Paid
                            </Typography>
                        </TableCell>
                        <TableCell sx={amountCellSx}>{formatAmount(vehicleFinal)}</TableCell>
                        {!ignoreLastYear && (
                            <TableCell sx={amountCellSx}>{formatAmount(vehicleLastYear)}</TableCell>
                        )}
                    </TableRow>

                    <TableRow>
                        <TableCell>
                            <Typography variant="body2" sx={labelCellSx}>
                                Per {statement}
                            </Typography>
                        </TableCell>
                        <TableCell sx={amountCellSx}>{formatAmount(auditedValue)}</TableCell>
                        {!ignoreLastYear && (
                            <TableCell sx={amountCellSx}>{formatAmount(lastYearAudited)}</TableCell>
                        )}
                    </TableRow>

                    <TableRow>
                        <TableCell>
                            <Typography variant="body2" sx={labelCellSx}>
                                Control
                            </Typography>
                        </TableCell>
                        <TableCell sx={diffCellSx(currentOk)}>{formatAmount(currentDiff)}</TableCell>
                        {!ignoreLastYear && (
                            <TableCell sx={diffCellSx(lastYearOk)}>{formatAmount(lastYearDiff)}</TableCell>
                        )}
                    </TableRow>
                </TableBody>
            </Table>
        </Box>
    );
}
